import { z } from "zod";
import type { Action, InputType, InputValue } from "../schemas/index.js";

const emailCheck = z.email();

/**
 * Checks and converts one value for an input of `type`. Numbers and yes/no
 * values may arrive as strings (from a model or a form) and are converted.
 * Returns undefined when the value does not fit the type or is empty.
 */
export function coerceInputValue(type: InputType, value: InputValue): InputValue | undefined {
  switch (type) {
    case "string":
    case "text": {
      const text = String(value).trim();
      return text === "" ? undefined : text;
    }
    case "email": {
      const email = String(value).trim().toLowerCase();
      return emailCheck.safeParse(email).success ? email : undefined;
    }
    case "number": {
      if (typeof value === "boolean") return undefined;
      const number = typeof value === "number" ? value : Number(value.trim().replace(/,/g, ""));
      return typeof value === "string" && value.trim() === "" ? undefined : Number.isFinite(number) ? number : undefined;
    }
    case "boolean": {
      if (typeof value === "boolean") return value;
      const text = String(value).trim().toLowerCase();
      if (text === "true" || text === "yes") return true;
      if (text === "false" || text === "no") return false;
      return undefined;
    }
  }
}

/** A value as it appears in a trace sentence. */
export function displayValue(value: InputValue): string {
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

/** True when a member must type a value for the action, so it cannot run on many messages at once. */
export function hasRequiredExtractInput(action: Action): boolean {
  if (action.type === "hold" || action.type === "ignore") return false;
  return action.inputs.some((input) => input.source === "extract" && input.required);
}
